import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Destination, DestinationStatus } from '../../../shared/interfaces/destination';
import { TableRow } from '../../../table/interfaces/table';
import { Table } from '../../../table/Table';
import { ListComponentBaseProps } from '../../../entity-list/interface';
import { getDataIdGenerator } from '../../../shared/utils/dataId';
import { destinationTableColumns } from './DestinationListColumns';
import { useAnalyticsTracker } from '../../../tracking/hooks/useAnalyticsTracker';
import { DestinationTrackers } from '../../tracking/destination-trackers';

export interface DestinationListTableProps extends ListComponentBaseProps<Destination[]> {
  wizardMode?: boolean;
  onDestinationSelect?: (destination: Destination) => void;
}

export function DestinationListTable({
  listMode: mode,
  data: destinations,
  tableProps,
  onDestinationSelect,
  wizardMode,
  dataId
}: DestinationListTableProps) {
  const navigate = useNavigate();
  const { eventTrack } = useAnalyticsTracker();

  const dataIdGenerator = getDataIdGenerator('destination-list-table', dataId);

  const rows: TableRow<Destination>[] = useMemo(() => {
    return destinations.map(destination => ({
      data: destination,
      tabIndex: 0,
      id: destination.id,
      disabled: wizardMode && destination.status.value === DestinationStatus.FAILED,
      role: 'button',
      dataId: dataIdGenerator(`row-${destination.id}`),
      onClick: (rowData: Destination) => {
        eventTrack({
          action: DestinationTrackers.DESTINATION_TRACKER_DESTINATION_LIST_DESTINATION_CLICK,
          properties: {
            destination: rowData.destinationType.destinationType,
            destinationId: rowData.id
          }
        });

        if (onDestinationSelect) {
          onDestinationSelect(rowData);
          return;
        }

        if (mode === 'standalone') {
          navigate(`/destination/edge/${destination.id}/configuration`);
        }
      }
    }));
  }, [destinations]);

  return <Table columns={destinationTableColumns} rows={rows} {...tableProps} dataId={dataId} />;
}
